import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tooltip, CircularProgress, Alert, AlertTitle,
  ToggleButtonGroup, ToggleButton, TextField, Button, MenuItem, Select,
  FormControl, InputLabel,
} from '@mui/material';
import { Refresh, WarningAmber } from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { getTriggerFailures, listAppContexts } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// Trigger Failures — account-wide trigger execution failures (#1187).
//
// Reads the owner-gated /developer/trigger-failures dev-API route: every app
// context by default, with an optional single-context filter — the partner
// API's GET /v1/trigger-failures is confined to whatever ONE context the
// portal's bearer is pinned to, exactly the confinement API Logs already
// avoids (ADR-0060 / #582).
//
// Cursor-paginated, "Load more" — a subject's failure history can be long and
// this is a metered read, so we never silently drain or truncate. Results are
// newest-first WITHIN a context; contexts are walked in a fixed order, not
// merged into one account-wide timeline (see the route's own doc for why —
// a cursor whose size doesn't grow with the account's context count).
//
// incomplete/contextsNotSearched: a context this page could not read (a
// transient fault) is named explicitly, aggregated across every loaded page —
// an honest gap must never read as "nothing failed".
//
// State is split, same as admin-app's TriggerFailuresPage: `pendingFilters`
// (what the operator is editing) vs. `appliedQuery` (env + filters the ACTIVE
// query is keyed on — null until the first Fetch). The query key embeds the
// applied snapshot directly, so `fetchNextPage()` always resumes the SAME
// query it started, never a live read of whatever the operator has since
// typed into an unapplied field — an infinite query mixing two different
// filter shapes under one cursor is exactly what the backend's cursor
// validation independently rejects as a 400, so this keeps the FE from ever
// constructing that request. A discrete pick (context/retryable Select, or
// the env toggle) commits immediately via `commitFilter`/`handleEnvChange`,
// which is a plain key change and therefore a fresh query at page one — no
// explicit reset needed. "Refresh", or "Fetch" with unchanged filters, goes
// through `rerunFromFirstPage` (`queryClient.resetQueries`, exact match) —
// never `refetch()`, which would re-walk every loaded page.
// ---------------------------------------------------------------------------

const RETRYABLE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Retryable' },
  { value: 'false', label: 'Not retryable' },
];

const ALL_CONTEXTS = '';
const PAGE_LIMIT = 50;

function defaultPendingFilters() {
  return {
    contextId: ALL_CONTEXTS,
    ruleId: '',
    category: '',
    retryable: '',
    from: '',
    to: '',
  };
}

function buildParams(query) {
  return {
    ...(query.contextId && { contextId: query.contextId }),
    ...(query.ruleId.trim() && { ruleId: query.ruleId.trim() }),
    ...(query.category.trim() && { category: query.category.trim() }),
    ...(query.retryable !== '' && { retryable: query.retryable === 'true' }),
    ...(query.from && { from: new Date(query.from).toISOString() }),
    ...(query.to && { to: new Date(query.to).toISOString() }),
    limit: PAGE_LIMIT,
  };
}

function CategoryChip({ category }) {
  if (!category) return <span style={{ color: '#9e9e9e' }}>—</span>;
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      fontSize: '0.7rem', fontWeight: 600,
      bgcolor: '#f3e5f5', color: '#6a1b9a',
    }}>
      {category}
    </Box>
  );
}

function RetryableChip({ retryable }) {
  if (retryable) {
    return <Typography component="span" variant="body2" color="text.disabled">Yes</Typography>;
  }
  return (
    <Chip label="No" size="small" color="warning" variant="outlined"
      sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
  );
}

export default function TriggerFailuresPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [env, setEnv]                 = useState('test');
  const [pendingFilters, setPendingFilters] = useState(defaultPendingFilters);
  const [appliedQuery, setAppliedQuery]     = useState(null); // null = not yet fetched

  const envTenantId = (e) => (e === 'live' ? user?.liveTenantId : user?.testTenantId);
  const tenantId = envTenantId(env);

  // Best-effort — a failure leaves the selector on "All contexts" only.
  const {
    data: contexts = [], isLoading: contextsLoading, isError: contextsError, error: contextsErrorObj,
  } = useQuery(
    ['devAppContexts', env],
    () => listAppContexts(env),
    { enabled: !!user },
  );

  const timeRangeInvalid = pendingFilters.from && pendingFilters.to
    && new Date(pendingFilters.from).getTime() >= new Date(pendingFilters.to).getTime();

  const queryKey = ['trigger-failures', appliedQuery];
  const {
    data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, error,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam }) =>
      getTriggerFailures(appliedQuery.env, { ...buildParams(appliedQuery), ...(pageParam ? { startFrom: pageParam } : {}) }),
    {
      enabled: appliedQuery !== null,
      getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
      staleTime: 0,
      // An infinite query's AUTOMATIC refetches (window refocus, network reconnect) re-fetch every
      // loaded page, exactly like refetch() — the same re-walk the header comment above says never
      // happens, through a different door. Disabled so that claim is actually true, matching
      // admin-app's TriggerFailuresPage.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const rows = useMemo(() => (data?.pages ?? []).flatMap((p) => p.data ?? []), [data]);
  const incomplete = useMemo(() => (data?.pages ?? []).some((p) => p.incomplete), [data]);
  const contextsNotSearched = useMemo(
    () => Array.from(new Set((data?.pages ?? []).flatMap((p) => p.contextsNotSearched ?? []))),
    [data],
  );
  const contextListUnavailable = useMemo(
    () => (data?.pages ?? []).some((p) => p.contextListUnavailable),
    [data],
  );
  const loaded = data !== undefined;

  /** Re-run the applied query from its FIRST page — not refetch(), which would re-walk every
   *  loaded page. Safe to call even when nothing has changed: resetQueries clears the cache
   *  entry for this exact key and lets `enabled` re-drive it from page one. */
  const rerunFromFirstPage = useCallback(() => {
    if (appliedQuery !== null) queryClient.resetQueries(['trigger-failures', appliedQuery], { exact: true });
  }, [appliedQuery, queryClient]);

  const handleFetch = () => {
    if (timeRangeInvalid || !tenantId) return;
    const next = { env, ...pendingFilters };
    if (appliedQuery !== null && JSON.stringify(next) === JSON.stringify(appliedQuery)) {
      // Same query, re-pressed — still re-run from page one rather than no-op.
      if (!isFetching) rerunFromFirstPage();
      return;
    }
    setAppliedQuery(next);
  };

  /** A discrete pick commits into BOTH pending and (if already fetched) applied state — the
   *  latter changes the query key, which is itself a fresh page-one query; no explicit reset. */
  const commitFilter = (field, value) => {
    setPendingFilters((prev) => ({ ...prev, [field]: value }));
    setAppliedQuery((prev) => (prev === null ? null : { ...prev, [field]: value }));
  };

  const handleEnvChange = (_, v) => {
    if (!v) return;
    setEnv(v);
    setPendingFilters((prev) => ({ ...prev, contextId: ALL_CONTEXTS }));
    setAppliedQuery((prev) => (prev === null ? null : { ...prev, env: v, contextId: ALL_CONTEXTS }));
  };

  const contextValue =
    pendingFilters.contextId === ALL_CONTEXTS || contexts.some((c) => c.contextId === pendingFilters.contextId)
      ? pendingFilters.contextId
      : ALL_CONTEXTS;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Trigger Failures</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Automations that ran and failed, across every app context in your account. A failure
        disappears once a later retry of the same firing succeeds — this shows what is currently
        broken, not a permanent history. Results are newest-first within a context; contexts are
        walked in a fixed order, not merged into one account-wide timeline.
      </Typography>

      {incomplete && (
        <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
          <AlertTitle>This page could not be read for every context — some failures may be missing.</AlertTitle>
          {contextsNotSearched.length > 0
            ? `Not searched: ${contextsNotSearched.join(', ')}`
            : contextListUnavailable
              ? "We couldn't load the list of app contexts to search."
              : 'A page of results came back only partially read — try Load more or Refresh.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup exclusive size="small" value={env} onChange={handleEnvChange}>
              <ToggleButton value="test" sx={{ fontSize: '0.8rem', px: 2 }}>Test</ToggleButton>
              <ToggleButton value="live" sx={{ fontSize: '0.8rem', px: 2 }}>Live</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="tf-context-label">Context</InputLabel>
              <Select
                labelId="tf-context-label"
                label="Context"
                value={contextValue}
                disabled={contextsLoading}
                onChange={(e) => commitFilter('contextId', e.target.value)}
              >
                <MenuItem value={ALL_CONTEXTS}><em>All contexts</em></MenuItem>
                {contexts.map((c) => (
                  <MenuItem key={c.contextId} value={c.contextId}>
                    {c.name ? `${c.contextId} — ${c.name}` : c.contextId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {contextsError && (
              <ApiErrorAlert error={contextsErrorObj} sx={{ flexBasis: '100%' }}>
                We couldn't load your app contexts, so the context filter is unavailable.
              </ApiErrorAlert>
            )}

            <TextField size="small" label="Rule ID" value={pendingFilters.ruleId}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, ruleId: e.target.value }))}
              sx={{ minWidth: 180 }} />
            <TextField size="small" label="Category" value={pendingFilters.category}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, category: e.target.value }))}
              sx={{ minWidth: 160 }} />
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="tf-retryable-label">Retryable</InputLabel>
              <Select labelId="tf-retryable-label" value={pendingFilters.retryable} label="Retryable"
                onChange={(e) => commitFilter('retryable', e.target.value)}>
                {RETRYABLE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField size="small" type="datetime-local" label="From"
              value={pendingFilters.from}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              error={!!timeRangeInvalid}
              sx={{ width: { xs: '100%', sm: 220 } }} />
            <TextField size="small" type="datetime-local" label="To"
              value={pendingFilters.to}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              error={!!timeRangeInvalid}
              sx={{ width: { xs: '100%', sm: 220 } }} />

            <Button variant="contained" size="small" onClick={handleFetch}
              disabled={isFetching || !tenantId || !!timeRangeInvalid}
              startIcon={isFetching && !isFetchingNextPage
                ? <CircularProgress size={14} color="inherit" /> : <Refresh />}>
              {isFetching && !isFetchingNextPage ? 'Querying…' : 'Fetch failures'}
            </Button>
            <Button variant="outlined" size="small" onClick={rerunFromFirstPage}
              disabled={appliedQuery === null || isFetching}
              startIcon={<Refresh />}>
              Refresh
            </Button>

            {timeRangeInvalid && (
              <Typography variant="caption" color="error">
                Start time must be before end time.
              </Typography>
            )}
            {loaded && !isFetching && (
              <Typography variant="caption" color="text.secondary">
                {rows.length} failure{rows.length === 1 ? '' : 's'} loaded
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <ApiErrorAlert error={error} sx={{ mb: 2 }} />

      {isFetching && rows.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Each request does a BOUNDED amount of work, so a page can come back with zero rows while
          more of the account is still unsearched (hasNextPage true). That must not read as "nothing
          failed" — distinct copy + its own Load more, never the terminal empty state. */}
      {!isFetching && loaded && rows.length === 0 && hasNextPage && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No matches in the portion searched so far — there&apos;s more of your account left to check.
          </Typography>
          <Button variant="outlined" size="small" onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      {/* A page can terminate (no more cursor) while STILL incomplete — a context-list fault on the
          very first page, or every context faulting, both give zero rows + no cursor + incomplete.
          Not the same claim as "nothing failed" — gate the terminal state on !incomplete, offer retry. */}
      {!isFetching && loaded && rows.length === 0 && !hasNextPage && incomplete && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {contextListUnavailable
              ? "We couldn't load the list of app contexts to search. Try again."
              : "We couldn't complete this search — part of your account was not searched. Try again."}
          </Typography>
          <Button variant="outlined" size="small" onClick={rerunFromFirstPage} disabled={isFetching}>
            Retry
          </Button>
        </Box>
      )}

      {!isFetching && loaded && rows.length === 0 && !hasNextPage && !incomplete && (
        <Typography color="text.secondary">No trigger failures found.</Typography>
      )}

      {rows.length > 0 && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, width: 160 }}>First failed</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 130 }}>Context</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Rule</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 130 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 90 }}>Retryable</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 80 }}>Attempts</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.id || i} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.contextId || '—'}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.ruleName || r.ruleId || '—'}
                    </TableCell>
                    <TableCell><CategoryChip category={r.category} /></TableCell>
                    <TableCell><RetryableChip retryable={r.retryable} /></TableCell>
                    <TableCell>{r.attempts ?? '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={r.detail || r.correlationId || ''}>
                        <span>
                          {r.detail
                            ? r.detail
                            : r.correlationId
                              ? `${r.correlationId} (quote to support)`
                              : '—'}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {hasNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
              <Button variant="outlined" size="small" onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
